import type {
  LocalCleanupReason,
  ProtectedSurfacePort,
} from "./sessionGate.js";

export interface CleanupStoragePort {
  removeItem(key: string): void;
}

export interface ClearablePort {
  clear(): void;
}

export interface LocalCleanupOptions {
  readonly storage?: CleanupStoragePort | null;
  readonly legacyTokenKeys?: readonly string[];
  readonly wsAuthAdapter: ClearablePort;
  readonly identityCache: ClearablePort;
  readonly cancelPending: () => void;
  readonly protectedSurface: Pick<ProtectedSurfacePort, "unmount">;
  readonly clearTransientCaches?: () => void;
}

const DEFAULT_LEGACY_TOKEN_KEYS = [
  "gocanvas:auth",
  "gocanvas:auth:refresh",
] as const;

function defaultStorage(): CleanupStoragePort | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function bestEffort(action: () => void): void {
  try {
    action();
  } catch {
    // Cleanup continues so one unavailable browser facility cannot retain the rest.
  }
}

/**
 * Clear document-local identity state after session invalidation/logout.
 * Cookies remain server-owned and unsynchronised board data is deliberately
 * outside this function's allow-list.
 */
export function createLocalCleanup(
  options: LocalCleanupOptions,
): (reason: LocalCleanupReason) => void {
  const storage =
    options.storage === undefined ? defaultStorage() : options.storage;
  const legacyTokenKeys = options.legacyTokenKeys ?? DEFAULT_LEGACY_TOKEN_KEYS;

  return (_reason: LocalCleanupReason): void => {
    for (const key of legacyTokenKeys) {
      bestEffort(() => storage?.removeItem(key));
    }
    bestEffort(() => options.wsAuthAdapter.clear());
    bestEffort(() => options.identityCache.clear());
    bestEffort(options.cancelPending);
    bestEffort(() => options.protectedSurface.unmount());
    if (options.clearTransientCaches) bestEffort(options.clearTransientCaches);
  };
}
